import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import { CircularProgress, Typography, Switch } from '@mui/material'
import Navigation from '../settings-navigation/Navigation'
import { CustomButton } from '../../components/restyled-mui/CustomButton'
import {
  DataGrid,
  GridToolbarQuickFilter,
  GridToolbarContainer,
} from '@mui/x-data-grid'
import { getAllUsers } from '../../redux/actions/users'
import { connect, useDispatch } from 'react-redux'
import { getSettingsStyles } from '../../styles/settingsStyles'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'
import moment from 'moment-timezone'
import Api from '../../api'
import { changeStatus } from '../../redux/actions/status'
import { STATUS } from '../../redux/types'
import ConfirmationDialog from '../confirmation-dialog/Confirmation'

const useStyles = makeStyles(theme => ({
  ...getSettingsStyles(theme),
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    padding: '50px 0 20px 0',
  },

  container: {
    textAlign: 'center',
    width: '95%',
    margin: '0 auto',
    height: '85vh',
  },
  statusEnabled: {
    color: theme.palette.primary.main,
  },
  statusDisabled: {
    color: theme.palette.primary.main,
  },
  disabledRow: {
    backgroundColor: '#f0f0f0 !important',
    opacity: 0.6,
    pointerEvents: 'none',
    userSelect: 'none',
  },
}))

const overridesWeirdResizing = {
  '& .MuiDataGrid-main': {
    width: 0,
    minWidth: '100%',
  },
  // Remove the default pointer on row hover
  '& .MuiDataGrid-row:hover': {
    cursor: 'default',
  },

  // Only show pointer for the first cell (NAME column)
  '& .MuiDataGrid-cell:first-of-type:hover': {
    cursor: 'pointer',
    color: '#1976d2',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    backgroundColor: 'rgba(25, 118, 210, 0.06)',
  },

  // Only show pointer for cells that contain the Switch
  '& .MuiDataGrid-cell:has(.MuiSwitch-root:hover)': {
    cursor: 'pointer',
  },
}

const CustomToolbar = () => {
  return (
    <GridToolbarContainer
      style={{ display: 'flex', justifyContent: 'space-between' }}
    >
      <GridToolbarQuickFilter debounceMs={300} placeholder={'Search user...'} />
    </GridToolbarContainer>
  )
}

const Users = props => {
  const classes = useStyles(props)
  const location = useLocation()
  const dispatch = useDispatch()
  const { history, users, getAllUsers } = props

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    confirmCallback: null,
    cancelCallback: null,
    title: '',
    content: '',
    color: '',
  })

  const userTimezone = loadStateFromLocalStorage('timezone') || 'Etc/UTC'

  const userAddRole = (users || []).map(user => ({
    ...user,
    role:
      user.permissions && user.permissions.includes('settings') ? 'admin' : '',
  }))

  const executeToggle = async (userId, userName, isEnabled) => {
    try {
      const response = await Api.Users.updateUser({
        user_id: userId,
        enabled: !isEnabled,
      })

      if (response.status !== 200) {
        throw new Error(response.data?.msg || 'Unexpected error')
      }

      await getAllUsers()

      dispatch(
        changeStatus(
          STATUS.SUCCESS,
          `The status of ${userName} was successfully updated.`,
        ),
      )
      return true
    } catch (error) {
      const message =
        error.response?.status === 403
          ? 'You do not have permission to update this user.'
          : error.response?.data?.msg || 'Failed to update user status'

      dispatch(changeStatus(STATUS.ERROR, message))
      console.error('Toggle user status failed:', error)
      return false
    }
  }

  const renderStatusCell = params => {
    if (params.row.id === 1) return null

    const handleToggle = async event => {
      event.stopPropagation()

      const userId = params.row.id
      const userName = params.row.name
      const isEnabled = params.row.enabled
      const isEnabling = !isEnabled

      // enabling: no confirm
      if (isEnabling) {
        await executeToggle(userId, userName, isEnabled)
        return
      }

      // disabling: confirm dialog
      setConfirmDialog({
        open: true,
        title: 'Disable User',
        content: (
          <Typography>
            Are you sure you want to disable <b>{userName}</b>?
          </Typography>
        ),
        color: '#C63C3C',
        type: 'warning',
        confirmCallback: async () => {
          await executeToggle(userId, userName, isEnabled)
          setConfirmDialog(prev => ({ ...prev, open: false }))
        },
        cancelCallback: () =>
          setConfirmDialog(prev => ({ ...prev, open: false })),
      })
    }

    return (
      <div
        style={{
          pointerEvents: 'auto',
          padding: '2px 4px',
          borderRadius: 6,
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={e =>
          (e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)')
        }
        onMouseLeave={e =>
          (e.currentTarget.style.backgroundColor = 'transparent')
        }
      >
        <Switch
          checked={params.value}
          onClick={handleToggle}
          color="primary"
          size="small"
        />
      </div>
    )
  }

  const renderDateTime = params => {
    if (params.field === 'last_login') {
      if (!params.value || Number(params.value) === 0) {
        return 'No login record'
      }
      const formatted = moment
        .unix(params.value)
        .tz(userTimezone)
        .format('YYYY-MM-DD HH:mm')

      return formatted
    }
  }

  const columns = [
    {
      field: 'name',
      headerName: 'USERNAME',
      flex: 2,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      flex: 2,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'enabled',
      headerName: 'STATUS',
      flex: 1.5,
      headerClassName: 'super-app-theme--header',
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      renderCell: renderStatusCell,
    },
    {
      field: 'last_login',
      headerName: 'LAST LOGIN',
      flex: 2,
      headerClassName: 'super-app-theme--header',
      renderCell: renderDateTime,
    },
    {
      field: 'role',
      headerName: 'ROLE',
      flex: 1.5,
      headerClassName: 'super-app-theme--header',
    },
  ]

  useEffect(() => {
    if (!users) {
      getAllUsers()
    }
  }, [users, getAllUsers])

  const handleCreateUser = () => {
    history.push(`${location.pathname}/create`)
  }

  const handleEditUser = params => {
    if (['enabled', 'last_login', 'role'].includes(params.field)) return

    if (!params.row.enabled) return

    const user = users.find(user => user.id === params.row.id)
    history.push(`${location.pathname}/${user.id}`)
  }

  return (
    <Navigation history={history}>
      <ConfirmationDialog {...confirmDialog} />
      {users && userAddRole ? (
        <div className={classes.container}>
          <div className={classes.headerContainer}>
            <CustomButton
              onClick={handleCreateUser}
              variant="contained"
              disableElevation
              color="primary"
              className={classes.addNewButton}
            >
              Add new
            </CustomButton>
          </div>

          <DataGrid
            rows={userAddRole}
            rowHeight={30}
            columns={columns}
            getRowId={row => row.id}
            disableColumnSelector
            disableColumnMenu
            disableRowSelectionOnClick
            onCellClick={handleEditUser}
            autoPageSize
            filterMode="client"
            getRowClassName={params =>
              !params.row.enabled ? classes.disabledRow : ''
            }
            sx={overridesWeirdResizing}
            slots={{
              toolbar: CustomToolbar,
            }}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </div>
      )}
    </Navigation>
  )
}

const mapStateToProps = state => ({
  users: state.users,
})

export default connect(mapStateToProps, { getAllUsers })(Users)
